import { useQuery } from '@tanstack/react-query'
import { isAddress } from 'viem'

import { QUERY_KEYS } from '~/constants/react-query'
import { useOrgCompliance } from '~/hooks/compliance'
import { useBatchPayeeLookup } from '~/hooks/compliance/payeeBatchAddressLookup'
import { useActiveOrgId } from '~/hooks/org'
import { IAddress } from '~/types'

export function usePayeeLookupByAddress(address: IAddress | undefined) {
  const activeOrgId = useActiveOrgId()

  const batchLookup = useBatchPayeeLookup()

  const query = useQuery({
    queryKey: [QUERY_KEYS.PAYEE_LOOKUP_BY_ADDRESS, activeOrgId, address],
    queryFn: async () => {
      if (!activeOrgId) throw new Error('Org ID required')
      if (!address) throw new Error('No address provided')
      if (!batchLookup) throw new Error('Batch lookup not initialized')

      const result = await batchLookup.call({ address, orgId: activeOrgId })
      if (!result) {
        throw new Error('No data returned')
      }

      return result
    },
    enabled: !!address && isAddress(address) && !!activeOrgId && !!batchLookup,
    retry: false,
  })

  return {
    payee: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
  }
}

export function usePayeeLookupById(id: string | undefined) {
  const { payees, query: complianceQuery } = useOrgCompliance()
  const payee = payees.find((p) => p.id === id)

  return {
    payee,
    isVerified: payee?.isVerified,
    isUSBased: payee?.isUSBased,
    isIndividual: payee?.isIndividual,
    hasAcceptedInvitation: payee?.hasAcceptedInvitation,
    verifiedAt: payee?.verifiedAt,
    isLoading: complianceQuery.isLoading,
  }
}
